import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useEffect, useMemo } from "react";
import { type Control, useFormContext, useWatch } from "react-hook-form";
import { useGetUserSources } from "@/app/user-sources/api/hooks";
import { useResourceScope } from "@/lib/resource-context";
import type { McpServerCreateRequest } from "../../types";

type UserSourceSelectorProps = {
  control: Control<McpServerCreateRequest>;
  /**
   * Default to the first active source when none is chosen. Only ever sets a
   * value while `auth_type` is `user_source`, so a stale id can't leak into a
   * gateway whose audience isn't External.
   */
  autoSelectFirst?: boolean;
};

export function UserSourceSelector({
  control,
  autoSelectFirst = false,
}: UserSourceSelectorProps) {
  const scope = useResourceScope();
  const { data } = useGetUserSources();
  const { setValue } = useFormContext<McpServerCreateRequest>();

  const authType = useWatch({ control, name: "auth_type" });
  const currentSourceId = useWatch({ control, name: "user_source_id" });

  const allSources = data.data.items;
  const activeSources = useMemo(
    () => allSources.filter((s) => s.status === "active"),
    [allSources]
  );
  const hasActiveSources = activeSources.length > 0;

  const firstActiveSourceId = activeSources[0]?.id;
  useEffect(() => {
    if (!autoSelectFirst) {
      return;
    }
    // Hard guard: never assign a source unless the External audience is active.
    if (authType !== "user_source") {
      return;
    }
    if (currentSourceId || !firstActiveSourceId) {
      return;
    }
    setValue("user_source_id", firstActiveSourceId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [
    autoSelectFirst,
    authType,
    currentSourceId,
    firstActiveSourceId,
    setValue,
  ]);

  return (
    <FormField
      control={control}
      name="user_source_id"
      render={({ field }) => {
        // Look up the selected source against the full list (not just active).
        // If a gateway was saved with a source that has since been disabled,
        // we still want to surface it in the trigger — just visibly marked —
        // so the user knows what they have. Disabled sources never appear in
        // the dropdown list, so the only way to keep one is to leave it
        // untouched on edit.
        const selectedSource = allSources.find((s) => s.id === field.value);
        const selectedIsDisabled =
          selectedSource != null && selectedSource.status !== "active";

        return (
          <FormItem>
            {hasActiveSources || selectedSource ? (
              <Select
                onValueChange={(val) => field.onChange(val || null)}
                // Pass `null` (not `undefined`) for the empty value so the
                // Select is controlled from the first render. Base UI freezes
                // controlled/uncontrolled on mount based on `value !== undefined`;
                // with `undefined` the auto-selected source never updates the
                // trigger, leaving it stuck in muted placeholder styling.
                value={field.value ?? null}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a User Source">
                      {selectedSource ? (
                        <>
                          {selectedSource.name}
                          {selectedIsDisabled ? (
                            <span className="text-muted-foreground">
                              {" "}
                              (disabled)
                            </span>
                          ) : null}
                        </>
                      ) : null}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {activeSources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="rounded-md border border-border border-dashed bg-muted/20 p-3">
                <p className="font-medium text-foreground text-sm">
                  No User Sources yet
                </p>
                <p className="mt-0.5 text-muted-foreground text-xs leading-snug">
                  External users sign in through a User Source (your OIDC
                  identity provider). Create one in a new tab, then come back
                  and pick it here.
                </p>
              </div>
            )}
            <div className="mt-1.5">
              <Link
                className="inline-flex items-center gap-1.5 font-medium text-link text-sm hover:underline"
                params={{
                  organizationId: scope.organizationId,
                  projectId: scope.projectId,
                }}
                rel="noopener"
                search={{ create: true }}
                target="_blank"
                to="/orgs/$organizationId/projects/$projectId/user-sources"
              >
                <ExternalLink className="size-3.5" />
                Create a User Source
              </Link>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
